"use client"

import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { uploadDocument, updateDocument, fetchDocuments } from "../store/slices/documentSlice"
import { File, X } from "lucide-react"

const AddDocument = ({ setShowAddModal, editingDoc, onSubmitSuccess }) => {
  const dispatch = useDispatch()
  const { documents } = useSelector((state) => state.documents)
  const { user } = useSelector((state) => state.auth)

  const isStudent = user?.role === "student"
  const initialStudentId = editingDoc ? editingDoc.student_id : isStudent ? user?.id : ""
  const [formLoading, setFormLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadData, setUploadData] = useState({
    student_id: initialStudentId?.toString() || "",
    document_type: editingDoc ? editingDoc.doc_name : "",
  })
  const [message, setMessage] = useState({ text: "", type: "" })

  // Log setShowAddModal to debug
  console.log("setShowAddModal:", setShowAddModal)

  const handleInputChange = (e) => {
    setUploadData({
      ...uploadData,
      [e.target.name]: e.target.value,
    })
  }

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0])
  }

  const handleClose = () => {
    if (typeof setShowAddModal === "function") {
      setShowAddModal(false)
      setSelectedFile(null)
      setUploadData({
        student_id: isStudent ? user?.id?.toString() || "" : "",
        document_type: "",
      })
    } else {
      console.warn("setShowAddModal is not a function. Modal cannot be closed.")
      setMessage({ text: "Error: Unable to close modal. Please contact support.", type: "error" })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setFormLoading(true)

    // If student, force their own ID
    const effectiveStudentId = isStudent ? user?.id : Number(uploadData.student_id)
    if (!effectiveStudentId || isNaN(effectiveStudentId) || Number(effectiveStudentId) <= 0) {
      setMessage({ text: "Please enter a valid Student ID", type: "error" })
      setFormLoading(false)
      return
    }
    if (!uploadData.document_type) {
      setMessage({ text: "Please select a document name", type: "error" })
      setFormLoading(false)
      return
    }

    // Check for duplicates
    if (!editingDoc) {
      const existingDoc = documents.find(
        (doc) => doc.student_id === Number(effectiveStudentId) && doc.doc_name === uploadData.document_type,
      )
      if (existingDoc) {
        setMessage({ text: "Duplicate found: this document already exists for the student.", type: "error" })
        setFormLoading(false)
        return
      }
    } else {
      const duplicateOnEdit = documents.find(
        (doc) =>
          doc.student_id === Number(effectiveStudentId) &&
          doc.doc_name === uploadData.document_type &&
          doc.doc_id !== editingDoc.doc_id,
      )
      if (duplicateOnEdit) {
        setMessage({ text: "Duplicate found: another document has the same name for this student.", type: "error" })
        setFormLoading(false)
        return
      }
    }

    const formData = new FormData()
    if (selectedFile) formData.append("document", selectedFile)
    formData.append("student_id", effectiveStudentId)
    formData.append("doc_name", uploadData.document_type)

    const action = editingDoc
      ? dispatch(updateDocument({ id: editingDoc.doc_id, formData }))
      : dispatch(uploadDocument(formData))

    action
      .unwrap()
      .then(() => {
        setMessage({
          text: editingDoc ? "Document updated successfully!" : "Document uploaded successfully!",
          type: "success",
        })
        dispatch(fetchDocuments())
        if (typeof onSubmitSuccess === "function") {
          onSubmitSuccess(editingDoc ? "updated" : "uploaded")
        }
        if (typeof setShowAddModal === "function") {
          setShowAddModal(false)
        } else {
          console.warn("setShowAddModal is not a function. Modal cannot be closed after submission.")
        }
      })
      .catch((err) => {
        setMessage({ text: `Error ${editingDoc ? "updating" : "uploading"} document: ${err}`, type: "error" })
      })
      .finally(() => {
        setFormLoading(false)
        setSelectedFile(null)
        setUploadData({
          student_id: isStudent ? user?.id?.toString() || "" : "",
          document_type: "",
        })
        const fileInput = document.getElementById("fileInput")
        if (fileInput) fileInput.value = ""
      })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <File className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-foreground">
            {editingDoc ? "Edit Document" : "Upload New Document"}
          </h3>
        </div>
        <button
          onClick={handleClose}
          className="p-2 text-gray-600 hover:text-gray-800"
          disabled={formLoading}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
          }`}
        >
          <p className={message.type === "success" ? "text-green-700" : "text-red-700"}>{message.text}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Student ID:</label>
            <input
              type="number"
              name="student_id"
              value={uploadData.student_id}
              onChange={handleInputChange}
              required
              min="1"
              disabled={editingDoc || formLoading || isStudent}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Document Name:</label>
            <select
              name="document_type"
              value={uploadData.document_type}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Document Name</option>
              <option value="10th">10th</option>
              <option value="12th">12th</option>
              <option value="PAN">PAN</option>
              <option value="Aadhaar">Aadhaar</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Select File:</label>
            <input
              id="fileInput"
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {selectedFile && <div className="mt-2 text-sm text-gray-600">Selected: {selectedFile.name}</div>}
          </div>
        </div>
        <div className="mt-6 flex space-x-4">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            disabled={formLoading}
          >
            {editingDoc ? "Update Document" : "Upload Document"}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-200"
            disabled={formLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddDocument